import {
  IconButton as MuiIconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useDesignTokens } from "../../../theme/useDesignTokens";

/**
 * Common IconButton Component
 * Supports variants, loading, tooltip, sizes
 */
const IconButton = ({
  variant = "primary",
  size = "small",
  icon,
  loading = false,
  disabled = false,
  tooltip = "",
  onClick,
  sx = {},
  className = "",
  isNotAllowed = false,
  ...props
}) => {
  const { colors, radius, border } = useDesignTokens();
  const prMain = colors?.primary?.main;
  const secMain = colors?.secondary?.main;
  const erMain = colors?.error?.main;
  const sucMain = colors?.success?.main;
  const inMain = colors?.info?.main;
  const disMain = colors?.disabled?.main;
  const ghMain = colors?.ghost?.main;
  const ghBg = colors?.ghost?.bgTransparent;

  const bdrOnepx = border?.onePx;

  const baseStyles = {
    padding: "6px",
    borderRadius: radius?.default,
    border: bdrOnepx + ghBg,
    transition: "all 0.2s ease",
  };

  const constStyle = {
    backgroundColor: ghBg,
    // outline: "none",
  };

  const variantStyles = {
    primary: {
      color: prMain,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + prMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
    secondary: {
      color: secMain,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + secMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
    success: {
      color: sucMain,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + sucMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
    danger: {
      color: erMain,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + erMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
    disabled: {
      color: disMain,
      cursor: isNotAllowed ? "not-allowed" : "pointer",
      // pointerEvents: "auto",
      pointerEvents: isNotAllowed ? "none" : "auto",
      opacity: 0.6,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + disMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
    info: {
      color: inMain,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + inMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
    ghost: {
      color: ghMain,
      "&:hover": {
        backgroundColor: ghBg,
        border: bdrOnepx + ghMain,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": constStyle,
    },
  };

  const sizeStyles = {
    small: {
      padding: "6px",
    },
    medium: {
      padding: "8px",
    },
    large: {
      padding: "10px",
    },
  };

  const finalSx = {
    ...baseStyles,
    ...(variantStyles[variant] || variantStyles.primary),
    ...(sizeStyles[size] || sizeStyles.small),
    ...sx,
  };

  const button = (
    <MuiIconButton
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      sx={finalSx}
      className={className}
      {...props}
    >
      {loading ? <CircularProgress size={16} color="inherit" /> : icon}
    </MuiIconButton>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

export default IconButton;
